import { Extension } from "@tiptap/core";
import { NodeSelection, Plugin, PluginKey, Selection, TextSelection } from "@tiptap/pm/state";
import { tabbable } from "tabbable";

import { isHiddenByCollapse } from "@/components/tiptap/collapse/buildCollapseRunDecorations";
import { editorTranslator } from "@/components/tiptap/EditorTranslator";
import { menuBlockAt } from "@/components/tiptap/line-option-buttons/lineMenuBlocks";
import { SCREEN_SM } from "@/style/breakpoints";
import { getIsTouchDevice } from "@/utils/getIsTouchDevice";
import { displayHotkey, matchHotkey } from "@/utils/hotkeys";
import { keyMatcher } from "@/utils/keyMatcher";

import type { DocumentModel } from "@/models/DocumentModel";
import type { Editor } from "@tiptap/core";
import type { EditorState, SelectionBookmark, Transaction } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

type BlockNavigationOptions = {
  doc: DocumentModel;
};

type BlockControlsState = {
  /**
   * The block whose controls are open while editing; in a read-only document, the block the
   * keyboard last moved to.
   */
  block: number | null;
  /** Where the caret was in the content when Tab moved it to the title, to return there. */
  contentSelection: SelectionBookmark | null;
  /** Whether focus last moved within the editor, so returning to it skips the keyboard hint. */
  isHintMuted: boolean;
  /** Whether the block menu is open, whose items may move focus into the editor. */
  isMenuOpen: boolean;
};

const INITIAL_STATE: BlockControlsState = {
  block: null,
  contentSelection: null,
  isHintMuted: false,
  isMenuOpen: false,
};

export const blockNavigationKey = new PluginKey<BlockControlsState>("blockNavigation");

/** Dispatched on the editor DOM to move focus to the current block's menu button. */
export const FOCUS_BLOCK_MENU_EVENT = "blocknavigation:focusmenu";

const BLOCK_MENU = ".line-option-overlay";

/** The title comes first and keeps its own Tab stops, so the controls cover the blocks after it. */
const FIRST_BLOCK_INDEX = 1;

function controlsState(state: EditorState): BlockControlsState {
  return blockNavigationKey.getState(state) ?? INITIAL_STATE;
}

/**
 * Whether the transaction removed the block at `pos`. Setting its attributes replaces its tokens
 * but keeps its content, and deleting its text keeps its tokens, so both the content start and the
 * end have to be removed.
 */
function isBlockDeleted(tr: Transaction, pos: number): boolean {
  const node = tr.before.nodeAt(pos);

  return (
    !!node &&
    tr.mapping.mapResult(pos + 1, 1).deleted &&
    tr.mapping.mapResult(pos + node.nodeSize, -1).deleted
  );
}

function blockPosAtIndex(state: EditorState, index: number): number {
  return state.doc.resolve(0).posAtIndex(index, 0);
}

/** The first content block from `index` on, in `direction`, that collapsing doesn't hide. */
function visibleBlock(state: EditorState, index: number, direction: 1 | -1): number | null {
  for (let i = index; i >= FIRST_BLOCK_INDEX && i < state.doc.childCount; i += direction) {
    const pos = blockPosAtIndex(state, i);

    if (!isHiddenByCollapse(state, pos)) {
      return pos;
    }
  }

  return null;
}

/**
 * The content block the controls belong to: the one they were opened or moved to, else the one
 * holding the selection, else the first. A hidden block resolves to the bar or heading above it.
 */
export function currentBlock(state: EditorState): number | null {
  const { selection } = state;
  // A node selection's head is the node's end, which would resolve to the block after it.
  const selected = selection instanceof NodeSelection ? selection.from : selection.head;
  const pos = controlsState(state).block ?? selected;
  const index = Math.max(state.doc.resolve(pos).index(0), FIRST_BLOCK_INDEX);

  return visibleBlock(state, index, -1) ?? visibleBlock(state, FIRST_BLOCK_INDEX, 1);
}

/** Whether a block's controls are open, even while focus is in the block menu's popup. */
export function areBlockControlsOpen(state: EditorState): boolean {
  return controlsState(state).block !== null;
}

/** Whether focus is in the block menu, its popup included, which is portaled outside it. */
function isFocusInBlockMenu(): boolean {
  const { activeElement } = document;

  if (!activeElement) {
    return false;
  }

  if (activeElement.closest(BLOCK_MENU)) {
    return true;
  }

  const menu = activeElement.closest('[role="menu"]');
  return !!menu?.id && !!document.querySelector(`${BLOCK_MENU} [aria-controls="${menu.id}"]`);
}

/** Whether focus is on the current block's menu. */
export function isFocusInBlockControls(view: EditorView): boolean {
  if (view.editable && !areBlockControlsOpen(view.state)) {
    return false;
  }

  return isFocusInBlockMenu();
}

/** Moves focus to the current block's menu button, which renders on demand. */
export function focusBlockMenu(view: EditorView) {
  view.dom.dispatchEvent(new CustomEvent(FOCUS_BLOCK_MENU_EVENT, { bubbles: true }));
}

function setControlsState(view: EditorView, update: Partial<BlockControlsState>) {
  const { tr } = view.state;
  view.dispatch(tr.setMeta(blockNavigationKey, { ...controlsState(view.state), ...update }));
}

/** Tells the controls that the block menu opened or closed. */
export function setBlockMenuOpen(view: EditorView, isMenuOpen: boolean) {
  if (controlsState(view.state).isMenuOpen !== isMenuOpen) {
    setControlsState(view, { isMenuOpen });
  }
}

function setBlock(view: EditorView, block: number | null) {
  if (controlsState(view.state).block !== block) {
    setControlsState(view, { block });
  }
}

/** Tab from the title's last stop: the caret goes back into the content, or to its start. */
function enterContent(view: EditorView) {
  const { state } = view;

  if (state.doc.childCount <= FIRST_BLOCK_INDEX) {
    return;
  }

  const selection =
    controlsState(state).contentSelection?.resolve(state.doc) ??
    Selection.findFrom(state.doc.resolve(blockPosAtIndex(state, FIRST_BLOCK_INDEX)), 1);

  if (selection) {
    view.dispatch(
      state.tr
        .setSelection(selection)
        .setMeta(blockNavigationKey, { ...controlsState(state), contentSelection: null })
        .scrollIntoView(),
    );
  }
  view.focus();
}

/** Whether Tab from `target` would leave the title's Tab stops: the caret in it, then its links. */
function isLastTitleStop(view: EditorView, target: HTMLElement): boolean {
  const title = view.nodeDOM(0);

  // Without content blocks there is nowhere to return to, so Tab leaves the editor.
  if (!(title instanceof HTMLElement) || view.state.doc.childCount <= FIRST_BLOCK_INDEX) {
    return false;
  }

  const stops = tabbable(title);

  if (target === view.dom) {
    return !isCaretInContent(view.state) && stops.length === 0;
  }

  return stops.at(-1) === target;
}

/** The title has no block controls. */
function isCaretInContent(state: EditorState): boolean {
  return state.selection.$head.index(0) >= FIRST_BLOCK_INDEX;
}

/**
 * Opens the current block's controls, if it has a block menu for them: a block can opt out of it
 * (`lineMenu: "none"`), and touch devices show none. Otherwise the inert editor would be left
 * with nothing to focus. Returns whether the controls opened.
 */
function openControls(editor: Editor, doc: DocumentModel): boolean {
  const { view } = editor;
  const pos = currentBlock(view.state);

  // The block menu is hidden below `SCREEN_SM`, so the controls would have no button to focus.
  if (
    !isCaretInContent(view.state) ||
    getIsTouchDevice() ||
    window.innerWidth < SCREEN_SM ||
    pos === null ||
    menuBlockAt(editor, pos, { withCollapsed: true }) === null
  ) {
    return false;
  }

  // Ends math editing and drops pending suggestions, which would keep taking the keys.
  doc.editor.reset(false);
  setBlock(view, pos);
  focusBlockMenu(view);
  return true;
}

/** Opens the current block's controls from math input, whose keys skip the editor DOM. */
export function openBlockControls(doc: DocumentModel): boolean {
  const editor = doc.editor.tiptap;
  return !!editor?.isEditable && openControls(editor, doc);
}

/** Back to editing, with the caret where the selection was left. */
function returnToEditing(view: EditorView) {
  setBlock(view, null);
  view.focus();
}

/** Shows the controls of the block at `pos`, as when its menu button takes focus by pointer. */
export function setControlsBlock(view: EditorView, pos: number) {
  setBlock(view, pos);
}

/** The first block from `index` on, in `direction`, that shows a block menu. */
function menuBlockFrom(editor: Editor, index: number, direction: 1 | -1): number | null {
  const { state } = editor;

  for (let i = index; i >= FIRST_BLOCK_INDEX && i < state.doc.childCount; i += direction) {
    const pos = blockPosAtIndex(state, i);

    if (!isHiddenByCollapse(state, pos) && menuBlockAt(editor, pos, { withCollapsed: true })) {
      return pos;
    }
  }

  return null;
}

/**
 * The current block if it shows a menu, else the nearest one after it that does, else before it,
 * so a block without a menu, like a change request, never leaves the editor without one.
 */
export function currentMenuBlock(editor: Editor): number | null {
  const pos = currentBlock(editor.state);

  if (pos === null) {
    return null;
  }

  const index = editor.state.doc.resolve(pos).index(0);
  return menuBlockFrom(editor, index, 1) ?? menuBlockFrom(editor, index, -1);
}

function moveToBlock(view: EditorView, pos: number | null) {
  if (pos === null) {
    return;
  }

  setBlock(view, pos);
  // The focused menu button only moves, so focusing it again wouldn't scroll it into view.
  const dom = view.nodeDOM(pos);

  if (dom instanceof HTMLElement) {
    dom.scrollIntoView({ block: "nearest" });
  }
  focusBlockMenu(view);
}

/** Handles a key on the current block's menu button. Returns whether the key was handled. */
export function handleBlockControlKey(editor: Editor, event: KeyboardEvent): boolean {
  const { view } = editor;
  const { state } = view;

  if (event.key === "Escape" || matchHotkey("block-controls", event)) {
    if (view.editable) {
      returnToEditing(view);
    }
    return true;
  }

  if (event.altKey || event.ctrlKey || event.metaKey) {
    return false;
  }

  switch (event.key) {
    // Inside the contenteditable root, they would move the caret behind the controls.
    case "ArrowLeft":
    case "ArrowRight": {
      return true;
    }

    case "ArrowUp":
    case "ArrowDown": {
      // While editing, the controls belong to one block; the menu opens with Enter or Space only.
      if (view.editable) {
        return true;
      }

      const direction = event.key === "ArrowDown" ? 1 : -1;
      const current = currentBlock(state);

      if (current !== null) {
        const index = state.doc.resolve(current).index(0) + direction;
        moveToBlock(view, menuBlockFrom(editor, index, direction));
      }
      return true;
    }

    case "Home":
    case "End": {
      if (view.editable) {
        return false;
      }

      moveToBlock(
        view,
        event.key === "Home"
          ? menuBlockFrom(editor, FIRST_BLOCK_INDEX, 1)
          : menuBlockFrom(editor, state.doc.childCount - 1, -1),
      );
      return true;
    }
  }

  return false;
}

/**
 * Keyboard access to the block menu, which has the gutter's actions (the gutter is hidden from
 * screen readers), so keyboard and screen reader users don't need a second, persistent mode next
 * to editing.
 *
 * - While editing, Mod+Alt+Period moves focus to the current block's menu button, and Escape or
 *   the same keys return to editing with the caret where it was. Meanwhile the editor is inert,
 *   so Tab and Shift+Tab leave it in either direction (Tab indents while editing).
 * - In a read-only document, the current block's menu button is the editor's Tab stop, after the
 *   title's: Up, Down, Home and End move between the blocks' menu buttons.
 */
export const BlockNavigation = Extension.create<BlockNavigationOptions>({
  name: "blockNavigation",

  addProseMirrorPlugins() {
    const { editor } = this;
    const { doc } = this.options;

    return [
      new Plugin<BlockControlsState>({
        key: blockNavigationKey,

        state: {
          init: () => INITIAL_STATE,
          apply(tr, value) {
            const meta = tr.getMeta(blockNavigationKey) as BlockControlsState | undefined;

            if (meta) {
              return meta;
            }

            if (!tr.docChanged) {
              return value;
            }

            const isDeleted = value.block !== null && isBlockDeleted(tr, value.block);

            return {
              ...value,
              block: value.block === null || isDeleted ? null : tr.mapping.map(value.block),
              contentSelection: value.contentSelection?.map(tr.mapping) ?? null,
              // The deleted block's menu goes with it, without reporting that it closed.
              isMenuOpen: value.isMenuOpen && !isDeleted,
            };
          },
        },

        props: {
          attributes: (state): Record<string, string> => {
            const t = editorTranslator(editor);
            const attributes: Record<string, string> = {};

            // While its controls have focus, the editor is inert, like the page behind a dialog,
            // so Tab and Shift+Tab leave it.
            const { isMenuOpen } = controlsState(state);

            if (editor.isEditable && areBlockControlsOpen(state) && !isMenuOpen) {
              attributes.inert = "";
            }

            if (t && !controlsState(state).isHintMuted) {
              attributes["aria-description"] = editor.isEditable
                ? t("keyboardHint", {
                    shortcut: displayHotkey("block-controls", {
                      spoken: (name) => t(`spokenKeys.${name}`),
                    }),
                  })
                : t("readOnlyKeyboardHint");
            }

            return attributes;
          },

          handleKeyDown: (view, event) =>
            view.editable && matchHotkey("block-controls", event) && openControls(editor, doc),
        },

        view(view) {
          // A focus that arrives while Tab is down is caused by it: focus moves before the key
          // comes back up.
          let isTabDown = false;

          const onWindowKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Tab") {
              isTabDown = true;
            }
          };

          const onWindowKeyUp = (event: KeyboardEvent) => {
            if (event.key === "Tab") {
              isTabDown = false;
            }
          };

          // A release outside the window never reaches it.
          const onWindowBlur = () => {
            isTabDown = false;
          };

          // Tabbing into the editor edits the title, whose Tab stops come first. Focus coming back
          // from inside the editor, like math handing it back on Tab, isn't entering it; math's input
          // is gone by then, so the hint's record of where focus was tells them apart.
          const onFocus = () => {
            if (!isTabDown || !view.editable || controlsState(view.state).isHintMuted) {
              return;
            }

            const { state } = view;
            const title = state.doc.firstChild;

            if (title) {
              view.dispatch(
                state.tr
                  .setSelection(TextSelection.create(state.doc, title.nodeSize - 1))
                  .setMeta(blockNavigationKey, {
                    ...controlsState(state),
                    contentSelection: isCaretInContent(state)
                      ? state.selection.getBookmark()
                      : controlsState(state).contentSelection,
                  }),
              );
            }
          };

          // Focus moving anywhere but the controls, such as a click into the text, closes them.
          const onDocumentFocusIn = (event: FocusEvent) => {
            // The hint is announced when focus comes to the editor from outside, but not on every
            // return from inside it, like from math input or the block menu.
            const editorRoot = view.dom.parentElement ?? view.dom;

            if (event.target instanceof Node && event.target !== view.dom) {
              const isHintMuted = editorRoot.contains(event.target);

              if (controlsState(view.state).isHintMuted !== isHintMuted) {
                setControlsState(view, { isHintMuted });
              }
            }

            if (
              view.editable &&
              areBlockControlsOpen(view.state) &&
              !isFocusInBlockControls(view)
            ) {
              setBlock(view, null);
            }
          };

          // A press in the editor closes the controls. While they have focus the editor is inert
          // and gets no pointer events, so the press places the caret itself, as ProseMirror would
          // have; with the menu open, the editor isn't inert and ProseMirror handles the press.
          const onDocumentPointerDown = (event: PointerEvent) => {
            const editorRoot = view.dom.parentElement;
            const { target } = event;

            if (
              !view.editable ||
              !areBlockControlsOpen(view.state) ||
              !(target instanceof Node) ||
              !editorRoot?.contains(target) ||
              (target instanceof Element && target.closest(BLOCK_MENU))
            ) {
              return;
            }

            const isInert = !controlsState(view.state).isMenuOpen;
            setBlock(view, null);

            if (!isInert) {
              return;
            }

            event.preventDefault();

            const found = view.posAtCoords({ left: event.clientX, top: event.clientY });

            if (found) {
              view.dispatch(
                view.state.tr.setSelection(Selection.near(view.state.doc.resolve(found.pos))),
              );
            }
            view.focus();
          };

          // Capture phase, before ProseMirror's keymaps, which would indent the title instead.
          const onKeyDown = (event: KeyboardEvent) => {
            const { target } = event;

            if (!(target instanceof HTMLElement)) {
              return;
            }

            if (view.editable && keyMatcher("Tab", event) && isLastTitleStop(view, target)) {
              event.preventDefault();
              event.stopImmediatePropagation();
              // This focus stays in the editor, so it must not count as Tab entering it.
              isTabDown = false;
              enterContent(view);
            }
          };

          window.addEventListener("keydown", onWindowKeyDown, true);
          window.addEventListener("keyup", onWindowKeyUp, true);
          window.addEventListener("blur", onWindowBlur);
          document.addEventListener("focusin", onDocumentFocusIn);
          document.addEventListener("pointerdown", onDocumentPointerDown, true);
          view.dom.addEventListener("focus", onFocus);
          view.dom.addEventListener("keydown", onKeyDown, true);

          return {
            destroy() {
              window.removeEventListener("keydown", onWindowKeyDown, true);
              window.removeEventListener("keyup", onWindowKeyUp, true);
              window.removeEventListener("blur", onWindowBlur);
              document.removeEventListener("focusin", onDocumentFocusIn);
              document.removeEventListener("pointerdown", onDocumentPointerDown, true);
              view.dom.removeEventListener("focus", onFocus);
              view.dom.removeEventListener("keydown", onKeyDown, true);
            },
          };
        },
      }),
    ];
  },
});
